import { useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import UploadZone from "@/components/UploadZone";
import { StatusBadge } from "@/components/StatusBadge";
import { Package, CircleDollarSign, Wallet, Loader2, Search, FileDown, Receipt } from "lucide-react";
import { CargusSetupBanner } from "@/components/CargusSetupBanner";

const STATE = {
  livrat: { type: "livrat", label: "Livrat" },
  retur: { type: "retur", label: "Retur" },
  livrare: { type: "livrare", label: "Spre destinatar" },
  depozit: { type: "depozit", label: "În depozit" },
  tranzit: { type: "tranzit", label: "În tranzit" },
  preluat: { type: "preluat", label: "Preluat" },
  netrimis: { type: "netrimis", label: "Netrimisă" },
  anulat: { type: "anulat", label: "Anulat" },
  in_tranzit: { type: "tranzit", label: "În tranzit" },
};

const FILTERS = [
  { id: "all", label: "Toate" },
  { id: "paid", label: "Plătite" },
  { id: "unpaid", label: "Neplătite" },
];

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="border border-border rounded-sm bg-card p-4">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon size={16} style={{ color }} />
      </div>
      <div className="mt-2 text-3xl font-mono font-semibold tracking-tight" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

export default function DeconturiVerify() {
  const [files, setFiles] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const stats = useMemo(() => {
    const a = batch?.awbs || [];
    const uncollected = a
      .filter((x) => x.paid === false)
      .reduce((s, x) => s + (parseFloat(String(x.amount || "0").replace(",", ".")) || 0), 0);
    return {
      total: a.length,
      paid: a.filter((x) => x.paid).length,
      unpaid: a.filter((x) => !x.paid).length,
      uncollected,
    };
  }, [batch]);

  const filtered = useMemo(() => {
    let a = batch?.awbs || [];
    if (filter === "paid") a = a.filter((x) => x.paid);
    if (filter === "unpaid") a = a.filter((x) => !x.paid);
    if (query.trim())
      a = a.filter(
        (x) => x.awb.includes(query.trim()) || (x.receiver || "").toLowerCase().includes(query.trim().toLowerCase())
      );
    return a;
  }, [batch, filter, query]);

  const run = async () => {
    if (!files.length) {
      toast.error("Adaugă fișierele cu deconturi.");
      return;
    }
    if (!fromDate || !toDate) {
      toast.error("Completează intervalul de date (de la / până la).");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      fd.append("from_date", fromDate);
      fd.append("to_date", toDate);
      const { data } = await api.post("/verify/deconturi", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBatch(data);
      toast.success(`${data.awbs.length} colete găsite în Cargus pentru interval`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadUnpaidPdf = async () => {
    try {
      const res = await api.get(`/batches/${batch.id}/unpaid-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "comenzi_neplatite.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Nu s-a putut genera PDF-ul");
    }
  };

  const field =
    "mt-1 w-full bg-card border border-border rounded-sm px-2.5 py-2 text-sm font-mono outline-none focus:border-primary";

  return (
    <Layout>
      <div className="p-8 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-2 mb-1">
          <Receipt size={20} />
          <h1 className="text-3xl font-semibold tracking-tight">Verificare rapidă din deconturi</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1 mb-8">
          Fără AWB-uri. Pui intervalul de date + deconturile tale, iar Cargus îți spune ce s-a încasat.
          Ce e în interval și în deconturi = Plătit; ce e în interval dar lipsește din deconturi = Neplătit.
        </p>

        <CargusSetupBanner />

        <div className="border border-border rounded-sm bg-card p-5 mb-8 max-w-2xl">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider">De la data</label>
              <input
                data-testid="dq-from-date"
                type="text"
                inputMode="numeric"
                placeholder="zz.ll.aaaa"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={field}
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Până la data</label>
              <input
                data-testid="dq-to-date"
                type="text"
                inputMode="numeric"
                placeholder="zz.ll.aaaa"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={field}
              />
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground font-mono mt-2 mb-4">
            Scrie liber: ex. 11.09.2026 (zz.ll.aaaa).
          </p>

          <UploadZone
            testid="dq-upload"
            files={files}
            setFiles={setFiles}
            exts={[".pdf", ".xls", ".xlsx"]}
            accept=".pdf,.xls,.xlsx"
            hint="PDF sau Excel (.xls/.xlsx) cu deconturile Cargus"
          />

          <button
            data-testid="dq-verify-btn"
            disabled={loading}
            onClick={run}
            className="mt-4 w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-sm px-3 py-2.5 text-sm font-medium hover:bg-white/90 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <CircleDollarSign size={16} />}
            Verifică din deconturi
          </button>
        </div>

        {batch && (
          <div className="animate-fade-up">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Stat icon={Package} label="Colete în interval" value={stats.total} color="#FFFFFF" />
              <Stat icon={CircleDollarSign} label="Plătite" value={stats.paid} color="#4DA3FF" />
              <Stat icon={Wallet} label="Neplătite" value={stats.unpaid} color="#FFCC00" />
              <Stat icon={Wallet} label="Neîncasat (lei)" value={stats.uncollected.toFixed(2)} color="#FF9F0A" />
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="flex gap-1.5">
                {FILTERS.map((f) => (
                  <button
                    key={f.id}
                    data-testid={`dq-filter-${f.id}`}
                    onClick={() => setFilter(f.id)}
                    className={`px-3 py-1.5 rounded-sm text-xs font-mono border transition-colors ${
                      filter === f.id
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground"
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="relative ml-auto">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="dq-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Caută AWB sau nume..."
                  className="bg-card border border-border rounded-sm pl-9 pr-3 py-1.5 text-sm font-mono outline-none focus:border-primary w-64"
                />
              </div>
              <button
                data-testid="dq-download-unpaid"
                onClick={downloadUnpaidPdf}
                className="flex items-center gap-2 border border-[#FFCC00]/40 text-[#FFCC00] rounded-sm px-3 py-1.5 text-xs font-mono hover:bg-[#FFCC00]/10 transition-colors"
              >
                <FileDown size={14} />
                PDF neplătite ({stats.unpaid})
              </button>
            </div>

            <div className="border border-border rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-mono">
                  <thead className="bg-[#0c0c0e] sticky top-0">
                    <tr className="text-left text-muted-foreground text-xs uppercase tracking-wider">
                      <th className="px-4 py-3 font-medium">AWB</th>
                      <th className="px-4 py-3 font-medium">Destinatar</th>
                      <th className="px-4 py-3 font-medium">Livrare</th>
                      <th className="px-4 py-3 font-medium">Data</th>
                      <th className="px-4 py-3 font-medium">Plată</th>
                      <th className="px-4 py-3 font-medium text-right">Sumă ramburs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((a) => (
                      <tr
                        key={a.awb}
                        data-testid={`dq-row-${a.awb}`}
                        className="border-t border-border hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-4 py-3 font-medium">{a.awb}</td>
                        <td className="px-4 py-3 text-muted-foreground max-w-[220px] truncate" title={a.receiver}>
                          {a.receiver || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge type={(STATE[a.delivery_state] || STATE.netrimis).type}>
                            {(STATE[a.delivery_state] || STATE.netrimis).label}
                          </StatusBadge>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{a.date || "—"}</td>
                        <td className="px-4 py-3">
                          {a.paid ? (
                            <span className="flex items-center gap-1.5">
                              <StatusBadge type="paid">Plătit</StatusBadge>
                              {a.match && (
                                <span className="text-[10px] text-muted-foreground">
                                  {a.match === "nume"
                                    ? "(după nume)"
                                    : a.match === "borderou"
                                    ? "(după borderou)"
                                    : "(după AWB)"}
                                </span>
                              )}
                            </span>
                          ) : (
                            <StatusBadge type="unpaid">Neplătit</StatusBadge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">{a.amount ? `${a.amount}` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
